import { marked } from "marked";

export interface BlogPost {
  slug: string;
  lang: "en" | "es";
  title: string;
  description: string;
  date: string;
  translationKey: string;
  ogImage?: string;
  draft: boolean;
  contentHtml: string;
}

marked.use({ gfm: true, breaks: false });

function parseFrontmatter(raw: string): { data: Record<string, unknown>; content: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };
  const data: Record<string, unknown> = {};
  for (const line of match[1].split("\n")) {
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;
    const key = line.slice(0, colonIdx).trim();
    let val: unknown = line.slice(colonIdx + 1).trim();
    if (typeof val === "string" && ((val.startsWith('"') && val.endsWith('"')) || (val.startsWith("'") && val.endsWith("'")))) {
      val = val.slice(1, -1);
    }
    if (val === "true") val = true;
    else if (val === "false") val = false;
    data[key] = val;
  }
  return { data, content: match[2] };
}

// Import all markdown files at build time — no file-system access at runtime
const rawFiles = import.meta.glob("../content/blog/*.md", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

function compile(raw: string): BlogPost | null {
  const { data, content } = parseFrontmatter(raw);
  const { slug, lang, title, description, date, translationKey, ogImage, draft } = data as {
    slug?: string;
    lang?: "en" | "es";
    title?: string;
    description?: string;
    date?: string;
    translationKey?: string;
    ogImage?: string;
    draft?: boolean;
  };
  if (!slug || !lang || !title || !date || !translationKey) return null;
  const contentHtml = marked.parse(content) as string;
  return {
    slug,
    lang,
    title,
    description: description ?? "",
    date,
    translationKey,
    ogImage,
    draft: draft ?? false,
    contentHtml,
  };
}

const allPosts: BlogPost[] = Object.values(rawFiles)
  .map(compile)
  .filter((p): p is BlogPost => p !== null && !p.draft)
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

export function getPosts(lang: "en" | "es"): BlogPost[] {
  return allPosts.filter((p) => p.lang === lang);
}

export function getPost(slug: string, lang: "en" | "es"): BlogPost | undefined {
  return allPosts.find((p) => p.slug === slug && p.lang === lang);
}

export function getTranslation(translationKey: string, targetLang: "en" | "es"): BlogPost | undefined {
  return allPosts.find((p) => p.translationKey === translationKey && p.lang === targetLang);
}

export function getAllPosts(): BlogPost[] {
  return allPosts;
}
